import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactElement,
} from 'react';
import {
  SHOPPING_CATEGORIES,
  shoppingCategoryLabel,
  useShoppingList,
  type ShoppingCategory,
  type ShoppingListItem,
} from '@/lib/nutrition';

/**
 * Vista "Lista de la compra" del módulo Nutrición (M2.34.B).
 *
 * Recibe `weekStartISO` (YYYY-MM-DD, lunes ISO) y muestra la agregación de
 * ingredientes que el backend calcula a partir del plan + recetas vinculadas.
 *
 * Decisiones de diseño:
 *
 * - La marca "comprado" se persiste en localStorage por semana
 *   (key `tri-mentor:shopping:checked:{weekStartISO}`). NO viaja al backend
 *   porque es estado local efímero: si regeneras el plan, los items pueden
 *   cambiar y queremos empezar limpios. Para evitar marcas huérfanas
 *   (item que ya no existe), filtramos al cargar.
 *
 * - Cada item es expandible para mostrar las recetas que lo usan + cantidad
 *   por receta. Usamos `<details>/<summary>` nativos para no añadir
 *   complejidad de estado y obtener accesibilidad gratis.
 *
 * - El botón "Imprimir / PDF" usa `window.print()`. Aplicamos una clase
 *   `print:hidden` (Tailwind) en chrome no esencial; el sistema operativo
 *   ya ofrece "Guardar como PDF" desde el cuadro de impresión nativo en
 *   todos los navegadores modernos.
 *
 * - Empty states:
 *   - `reason='no_plan' | 'no_recipes_linked'` → CTA con texto explicativo.
 *   - Sin ingredientes (raro) → mismo CTA neutro.
 */
export function ShoppingListView({
  weekStartISO,
}: {
  weekStartISO: string;
}): ReactElement {
  const query = useShoppingList(weekStartISO);

  return (
    <section aria-label="Lista de la compra" className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">
            Lista de la compra
          </h2>
          <p className="text-sm text-slate-600">
            Semana del {formatWeekStart(weekStartISO)}
          </p>
        </div>
      </header>

      {query.isLoading && (
        <p role="status" aria-live="polite" className="text-slate-700">
          Cargando lista…
        </p>
      )}

      {query.isError && (
        <p
          role="alert"
          className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-800"
        >
          No se pudo cargar la lista. {query.error?.message ?? ''}
        </p>
      )}

      {query.data && (
        <ShoppingListBody data={query.data} weekStartISO={weekStartISO} />
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Body con estado local de marcado
// ---------------------------------------------------------------------------

function ShoppingListBody({
  data,
  weekStartISO,
}: {
  data: {
    items: ShoppingListItem[];
    reason?: 'no_plan' | 'no_recipes_linked' | undefined;
  };
  weekStartISO: string;
}): ReactElement {
  const items = data.items;
  const { checked, setChecked, markAll, clear } = useCheckedState(
    weekStartISO,
    items,
  );

  if (items.length === 0) {
    // `exactOptionalPropertyTypes` exige no pasar `undefined` explícito en
    // props opcionales — usamos spread condicional.
    return <EmptyState {...(data.reason ? { reason: data.reason } : {})} />;
  }

  const grouped = useMemo(() => groupByCategory(items), [items]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2 print:hidden">
        <button
          type="button"
          onClick={markAll}
          className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-2"
        >
          Marcar todo como comprado
        </button>
        <button
          type="button"
          onClick={clear}
          className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-2"
        >
          Limpiar
        </button>
        <button
          type="button"
          onClick={() => window.print()}
          className="rounded-md bg-teal-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-2"
        >
          Imprimir / Exportar PDF
        </button>
      </div>

      <div className="space-y-4">
        {grouped.map(({ category, items: catItems }) => (
          <CategoryGroup
            key={category}
            category={category}
            items={catItems}
            checked={checked}
            onToggle={setChecked}
          />
        ))}
      </div>
    </div>
  );
}

function CategoryGroup({
  category,
  items,
  checked,
  onToggle,
}: {
  category: ShoppingCategory;
  items: ShoppingListItem[];
  checked: Record<string, boolean>;
  onToggle: (name: string, value: boolean) => void;
}): ReactElement {
  return (
    <section
      aria-label={`Categoría ${shoppingCategoryLabel(category)}`}
      className="rounded-lg border border-slate-200 bg-white shadow-sm"
    >
      <header className="border-b border-slate-200 px-4 py-2">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-600">
          {shoppingCategoryLabel(category)}
        </h3>
      </header>
      <ul className="divide-y divide-slate-100">
        {items.map((it) => (
          <li key={it.name} className="px-4 py-2">
            <details>
              <summary className="flex cursor-pointer items-center gap-3 py-1">
                <input
                  type="checkbox"
                  checked={Boolean(checked[it.name])}
                  onChange={(e) => onToggle(it.name, e.target.checked)}
                  aria-label={`Comprado: ${it.name}`}
                  className="h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-600"
                  onClick={(e) => e.stopPropagation()}
                />
                <span
                  className={
                    checked[it.name]
                      ? 'flex-1 text-sm text-slate-400 line-through'
                      : 'flex-1 text-sm text-slate-900'
                  }
                >
                  {it.name}
                </span>
                <span className="text-sm tabular-nums text-slate-600">
                  {formatGrams(it.totalQuantityG)}
                </span>
              </summary>
              <ul className="ml-7 mt-1 space-y-0.5 text-xs text-slate-500">
                {it.recipes.map((r) => (
                  <li key={r.recipeTitle}>
                    {r.recipeTitle} — {formatGrams(r.qtyG)}
                  </li>
                ))}
              </ul>
            </details>
          </li>
        ))}
      </ul>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Empty state
// ---------------------------------------------------------------------------

function EmptyState({
  reason,
}: {
  reason?: 'no_plan' | 'no_recipes_linked';
}): ReactElement {
  const message =
    reason === 'no_recipes_linked'
      ? 'El plan de esta semana aún no tiene recetas vinculadas. Regenera el plan para activar la lista de la compra (M2.34.A).'
      : reason === 'no_plan'
        ? 'Genera primero un plan de comidas para esta semana.'
        : 'No hay ingredientes para esta semana.';
  return (
    <section
      aria-label="Sin lista"
      className="rounded-lg border border-dashed border-slate-300 bg-white px-6 py-10 text-center shadow-sm"
    >
      <p className="text-sm text-slate-700">{message}</p>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Hook de estado checked
// ---------------------------------------------------------------------------

function useCheckedState(
  weekStartISO: string,
  items: ShoppingListItem[],
): {
  checked: Record<string, boolean>;
  setChecked: (name: string, value: boolean) => void;
  markAll: () => void;
  clear: () => void;
} {
  const storageKey = `tri-mentor:shopping:checked:${weekStartISO}`;
  const [checked, setCheckedState] = useState<Record<string, boolean>>(() =>
    loadChecked(storageKey),
  );

  // Si cambia la semana, recargamos. Filtramos también marcas huérfanas.
  useEffect(() => {
    const loaded = loadChecked(storageKey);
    const itemNames = new Set(items.map((i) => i.name));
    const filtered: Record<string, boolean> = {};
    for (const [k, v] of Object.entries(loaded)) {
      if (itemNames.has(k)) filtered[k] = v;
    }
    setCheckedState(filtered);
    // El efecto se dispara cuando cambia la semana o el set de items
    // — intencionado para que la lista no muestre marcas huérfanas.
  }, [storageKey, items]);

  const persist = useCallback(
    (next: Record<string, boolean>) => {
      setCheckedState(next);
      try {
        localStorage.setItem(storageKey, JSON.stringify(next));
      } catch {
        /* quota / disabled — ignoramos, el estado en memoria sigue OK */
      }
    },
    [storageKey],
  );

  const setChecked = useCallback(
    (name: string, value: boolean) => {
      persist({ ...checked, [name]: value });
    },
    [checked, persist],
  );

  const markAll = useCallback(() => {
    const next: Record<string, boolean> = {};
    for (const it of items) next[it.name] = true;
    persist(next);
  }, [items, persist]);

  const clear = useCallback(() => {
    persist({});
  }, [persist]);

  return { checked, setChecked, markAll, clear };
}

function loadChecked(storageKey: string): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      const out: Record<string, boolean> = {};
      for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
        if (typeof v === 'boolean') out[k] = v;
      }
      return out;
    }
  } catch {
    /* swallow */
  }
  return {};
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatGrams(qtyG: number): string {
  if (qtyG === 0) return '—';
  if (qtyG >= 1000) {
    return `${(qtyG / 1000).toFixed(qtyG % 1000 === 0 ? 0 : 1)} kg`;
  }
  return `${Math.round(qtyG)} g`;
}

function groupByCategory(
  items: ShoppingListItem[],
): Array<{ category: ShoppingCategory; items: ShoppingListItem[] }> {
  const map = new Map<ShoppingCategory, ShoppingListItem[]>();
  for (const it of items) {
    const arr = map.get(it.category) ?? [];
    arr.push(it);
    map.set(it.category, arr);
  }
  // Orden canónico de categorías (mismo del backend).
  return SHOPPING_CATEGORIES.filter((c) => map.has(c)).map((c) => ({
    category: c,
    items: map.get(c)!,
  }));
}

function formatWeekStart(weekStartISO: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(weekStartISO);
  if (!m) return weekStartISO;
  const [, , mo, d] = m;
  return `${d}/${mo}`;
}
